import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Input,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';
import React, { useRef, Ref } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import fileSize from "filesize";
import { FormikProps } from "formik";

export interface FileSearchFormProps {
    submitButtonRef?: Ref<HTMLButtonElement>;
}

export enum FileSearchItemType {
    Item = "item",
    Container = "container",
    File = "file",
    Tape = "tape",
}

interface FileSearchItemTypeList {
    [key: string]: FileSearchItemType,
}

const fileSearchItemTypeOptions:FileSearchItemTypeList = {
    "Item": FileSearchItemType.Item,
    "Carton": FileSearchItemType.Container,
    "File": FileSearchItemType.File,
    "Tape": FileSearchItemType.Tape,
}

export enum FileSearchFieldType {
    Barcode = "barcode",
    AltCode = "altCode",
    AltCodeAccount = "altCodeAccount",
    TypeBarcode = "typeBarcode",
}

interface FileSearchFieldTypeList {
    [key: string]: FileSearchFieldType,
}

const fileSearchFieldTypeOptions:FileSearchFieldTypeList = {
    "Barcode": FileSearchFieldType.Barcode,
    "Alt. Code": FileSearchFieldType.AltCode,
    "Alt. Code and Account": FileSearchFieldType.AltCodeAccount,
    "Type and Code": FileSearchFieldType.TypeBarcode,
}

export enum FileSearchDelimiterType {
    VerticalBar = "verticalBar",
    Comma = "comma",
    Tab = "tab",
    Semicolon = "semicolon",
    Other = "other",
}

interface FileSearchDelimiterTypeList {
    [key: string]: FileSearchDelimiterType,
}

const fileSearchDelimiterTypeOptions:FileSearchDelimiterTypeList = {
    "Vertical bar": FileSearchDelimiterType.VerticalBar,
    "Comma": FileSearchDelimiterType.Comma,
    "Tab": FileSearchDelimiterType.Tab,
    "Semicolon": FileSearchDelimiterType.Semicolon,
    "Other": FileSearchDelimiterType.Other,
}

export interface FileSearchFormParams {
    selectedFile?: File;
    itemType: string;
    fieldType: string;
    delimiterType: string;
    delimiterChar: string;
}

export default function FileSearchForm(props: FileSearchFormProps & FormikProps<FileSearchFormParams>): JSX.Element {
    const {
        values,
        errors,
        handleSubmit,
        setFieldValue,
        submitButtonRef,
    } = props;

    const fileRef = useRef<HTMLInputElement>(null);

    return (
        <Box sx={{margin: 1}}>
            <form onSubmit={handleSubmit}>
                <button ref={submitButtonRef} type="submit" style={{display: "none"}}/>
                <Grid container>
                    <Grid item xs={12}>
                        <label htmlFor="fileSelector">
                            <Input
                                id="fileSelector"
                                inputProps={{accept: 'text/csv'}}
                                ref={fileRef}
                                type="file"
                                sx={{display: "none"}}
                                onChange={(value: React.ChangeEvent<HTMLInputElement>) => {
                                    if (value.currentTarget.files === null || value.currentTarget.files.length === 0) {
                                        setFieldValue("selectedFile", undefined);
                                    } else {
                                        setFieldValue("selectedFile", value.currentTarget.files[0]);
                                    }
                                }}
                            />
                            <TextField
                                disabled
                                variant="standard"
                                value={values.selectedFile !== undefined ? `${values.selectedFile.name} ${fileSize(values.selectedFile.size)}` : "No file selected"}
                                sx={{minWidth: "400px"}}
                            />
                            <Button variant="contained" component="span" sx={{marginLeft: 1}}>
                                Select File
                                <Box component={'span'} sx={{ width: '5px' }}/>
                                <FontAwesomeIcon icon={["fas", "folder"]}/>
                            </Button>
                        </label>
                    </Grid>
                    <Grid item xs={12}>
                        {Boolean(errors.selectedFile) &&
                            <Typography sx={{color: 'secondary.main'}}>{errors.selectedFile}</Typography>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl sx={{
                            marginTop: 1,
                            marginBottom: 1,
                            width: 550
                        }}>
                            <FormLabel>Item type</FormLabel>
                            <Select
                                name="itemType"
                                value={values.itemType}
                                onChange={(event) => setFieldValue("itemType", event.target.value)}
                                required
                            >
                                {
                                    Object.entries(fileSearchItemTypeOptions).map(([key, value]) => (
                                            <MenuItem key={value} value={value}>{key}</MenuItem>
                                        )
                                    )
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl sx={{
                            marginTop: 1,
                            marginBottom: 1,
                            width: 550
                        }}>
                            <FormLabel>Fields</FormLabel>
                            <Select
                                name="fieldType"
                                value={values.fieldType}
                                onChange={(event) => setFieldValue("fieldType", event.target.value)}
                                required
                            >
                                {
                                    Object.entries(fileSearchFieldTypeOptions).map(([key, value]) => (
                                            <MenuItem key={value} value={value}>{key}</MenuItem>
                                        )
                                    )
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box>
                            <FormControl
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 1,
                                    width: 550
                                }}
                                disabled={
                                    values.fieldType === FileSearchFieldType.Barcode ||
                                    values.fieldType === FileSearchFieldType.AltCode
                                }
                            >
                                <FormLabel>Delimiter</FormLabel>
                                <RadioGroup
                                    defaultValue="verticalBar"
                                    name="delimiterType"
                                    value={values.delimiterType}
                                    onChange={(_, value) => setFieldValue("delimiterType", value)}
                                    row
                                >
                                    {
                                        Object.entries(fileSearchDelimiterTypeOptions).map(([key, value]) => (
                                                <FormControlLabel key={value} value={value} control={<Radio/>} label={key}/>
                                            )
                                        )
                                    }
                                </RadioGroup>
                                <TextField
                                    name="delimiterChar"
                                    disabled={
                                        values.fieldType === FileSearchFieldType.Barcode ||
                                        values.fieldType === FileSearchFieldType.AltCode ||
                                        values.delimiterType !== FileSearchDelimiterType.Other
                                    }
                                    required={
                                        values.fieldType !== FileSearchFieldType.Barcode &&
                                        values.fieldType !== FileSearchFieldType.AltCode &&
                                        values.delimiterType === FileSearchDelimiterType.Other
                                    }
                                    value={values.delimiterChar}
                                    onChange={(event) => setFieldValue("delimiterChar", event.target.value)}
                                    inputProps={{
                                        maxLength: 1,
                                    }}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
}
